import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  Button,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useParams } from "react-router-dom";
import AdaptiveImage from "./adaptiveImage";
import ProductImageCell from "./productImageCell";
import { formattedDate } from "./sharedUtils";
import { getProducts, getProductCount, deleteProduct, saveProductSpecs, saveProductImages, saveProductShopifyCategory, saveProductEbayCategory } from "../databaseManager";
import { PRODUCTS_ENDPOINT } from "./api";
import ConfirmDeleteDialog from "./confirmDeleteDialog";
import { customerHasShopify, customerHasEbay, customerHasSmartsheet } from './sharedUtils';
import { useAuth } from "./authContext";
import { postProductToChannel, retryProduct, addProductImages } from './api';
import { encodeFileAsBase64, resizeImage, extractErrorMessage, isHeicFile, convertHEICToJPEG } from './sharedUtils';
import { conditionToValueMap, conditionDisplayLabel } from './productEditConstants';
import { SHOPIFY_CATEGORY_NAMES, SHOPIFY_CATEGORY_OPTIONS } from './products/shopifyCategoryOptions';

const DATA_FIELD_ORDER = {
  "Title": 0,
  "Description": 1,
  "Price": 2,
  "Category": 3,
  "Shopify Category": 3,
  "Condition": 4,
  "Item Length": 5,
  "Item Width": 6,
  "Item Height": 7,
  "Item Weight": 8,
  "Unit Quantity": 100,
  "Unit Type": 100,
  "California Prop 65 Warning": 100,
};

export default function ProductBulkEdit() {
  const navigate = useNavigate();
  const params = useParams();
  const { customer, shopikUser } = useAuth();
  const customerId = params["customer"];
  const productUri = params["productUri"]
  const [data, setData] = useState([]);
  const [editedData, setEditedData] = useState({});
  const [newFields, setNewFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [error, setError] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [imageConfirmOpen, setImageConfirmOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [images, setImages] = useState({});
  const [originalEditedData, setOriginalEditedData] = useState({});
  const [originalImages, setOriginalImages] = useState({});
  const [shopifyData, setShopifyData] = useState({});
  const [ebayData, setEbayData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingAction, setSubmittingAction] = useState(null); // 'shopify'|'ebay'|'smartsheet'|'retry'
  const [open, setOpen] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState({});
  const [uploadError, setUploadError] = useState(null);
  const [shopifyCats, setShopifyCats] = useState({});
  const [ebayCats, setEbayCats] = useState({});
  const [ebayCatOptions, setEbayCatOptions] = useState({});
  const [originalShopifyCats, setOriginalShopifyCats] = useState({});
  const [originalEbayCats, setOriginalEbayCats] = useState({});
  const addPhotoInputRef = React.useRef(null);
  const addPhotoTargetRef = React.useRef(null);
  
  const isEmptyVal = (v) => 
  v === null || v === undefined || (typeof v === "string" && v.trim() === "");
  const stripQuery = (u) => (u ? u.split("?")[0] : "");
  const imgKey = (img) => img?.id ?? stripQuery(img?.urlString ?? "");
// Sort specs by order, keep unknowns after knowns, and push empties to the end
  const sortSpecs = (specs = {}) => {
  const entries = Object.entries(specs);

  const nonEmpty = [];
  const empty = [];
  for (const [k, v] of entries) {
    (isEmptyVal(v) ? empty : nonEmpty).push([k, v]);
  }

  const byOrder = ([aKey], [bKey]) => {
    const a = DATA_FIELD_ORDER[aKey] ?? 1000;
    const b = DATA_FIELD_ORDER[bKey] ?? 1000;
    if (a !== b) return a - b;
    return aKey.localeCompare(bKey); // stable-ish tie-breaker
  };

  nonEmpty.sort(byOrder);
  empty.sort(byOrder);

  return Object.fromEntries([...nonEmpty, ...empty]);
};
const requestDelete = (productUri, img) => {
  setImageToDelete({ productUri, image: img });
  setImageConfirmOpen(true);
};

  const handleConfirmDelete = () => {
  const { productUri, image } = imageToDelete || {};
  if (!productUri || !image) return;

  setImages((prev) => {
    const prevArr = prev[productUri] || [];
    // mark hidden on the matched image (useful if you later persist this flag)
    const marked = prevArr.map((im) =>
      imgKey(im) === imgKey(image) ? { ...im, isHidden: true } : im
    );
    // remove hidden ones from what we keep in state for rendering
    const filtered = marked.filter((im) => !im.isHidden);

    return { ...prev, [productUri]: filtered };
  });

  setImageConfirmOpen(false);
  setImageToDelete(null);
};
  const handleShopifyPost = async (productId) => {
    setIsSubmitting(true);
    setSubmittingAction('shopify');
    try {
      await postProductToChannel(customer.apiKey, productId, 'shopify', {});
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to post product to Shopify'));
    }
    setIsSubmitting(false);
    setSubmittingAction(null);
  }

  const handleRetry = async (productId) => {
    setIsSubmitting(true);
    setSubmittingAction('retry');
    try {
      await retryProduct(customer.apiKey, productId, 0, " ");
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to retry product'));
    }
    setIsSubmitting(false);
    setSubmittingAction(null);
  }

  const viewProductPricing = (productId) => {
    navigate('/product-pricing/' + customerId + '/' + productId)
  };

  const handleEbayPost = async (productId) => {
    setIsSubmitting(true);
    setSubmittingAction('ebay');
    try {
      await postProductToChannel(customer.apiKey, productId, 'ebay', {});
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to post product to eBay'));
    }
    setIsSubmitting(false);
    setSubmittingAction(null);
  }

  const handleSmartsheetPost = async (productId) => {
    setIsSubmitting(true);
    setSubmittingAction('smartsheet');
    try {
      await postProductToChannel(customer.apiKey, productId, 'smartsheet', {});
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to post product to smartsheet'));
    }
    setIsSubmitting(false);
    setSubmittingAction(null);
  }


const fetchData = async () => {
      setLoading(true);

      if (productUri) {
        // single-product mode
        getProducts(1, null, customerId, "",
          (items) => {
            const [p] = items;
          
            setData([p]);
            setTotalItems(1);
            // init state for just this one product
            const initEdited = { [p.productUri]: sortSpecs(p.specs || {}) };
            const initNewFields = { [p.productUri]: { field: "", value: "" } };
            const initImages = { [p.productUri]: p.allImages };
            const initShopifyData = {};
            const initEbayData = {};

            setEditedData(initEdited);
            setNewFields(initNewFields);
            setImages(initImages);
            if (p.shopifyData) {
              initShopifyData[p.productUri] = p.shopifyData;
              setShopifyData(initShopifyData);
            }
            if (p.ebayData) {
              initEbayData[p.productUri] = p.ebayData;
              setEbayData(initEbayData);
            }

            const initShopifyCats = { [p.productUri]: p.shopifyCategory?.fullName || '' };
            const initEbayCats = { [p.productUri]: p.gptCategories?.primaryCategory || '' };
            const initEbayCatOptions = { [p.productUri]: p.gptCategories?.allPotentialCategories || [] };
            setShopifyCats(initShopifyCats);
            setEbayCats(initEbayCats);
            setEbayCatOptions(initEbayCatOptions);
            setOriginalShopifyCats(JSON.parse(JSON.stringify(initShopifyCats)));
            setOriginalEbayCats(JSON.parse(JSON.stringify(initEbayCats)));

            // baseline (deep clones)
            setOriginalEditedData(JSON.parse(JSON.stringify(initEdited)));
            setOriginalImages(JSON.parse(JSON.stringify(initImages)));
            setLoading(false);
            return;
          },
          false,
          { productUri }      // ← pass the id here
        );
        return ;
      }

      // bulk mode
      const count = await getProductCount(customerId);
      setTotalItems(count);
      getProducts(pageSize, null, customerId, "", (productList) => {
        setData(productList);
      
        const initEdited = {};
        const initNewFields = {};
        const initImages = {};
        const initShopifyData = {};
        const initEbayData = {};
        const initShopifyCats = {};
        const initEbayCats = {};
        const initEbayCatOptions = {};
        productList.forEach((p) => {
          initEdited[p.productUri] = sortSpecs(p.specs || {});
          initNewFields[p.productUri] = { field: "", value: "" };
          initImages[p.productUri] =  p.allImages;
          initShopifyCats[p.productUri] = p.shopifyCategory?.fullName || '';
          initEbayCats[p.productUri] = p.gptCategories?.primaryCategory || '';
          initEbayCatOptions[p.productUri] = p.gptCategories?.allPotentialCategories || [];
           if (p.shopifyData) {
              initShopifyData[p.productUri] = p.shopifyData;
              setShopifyData(initShopifyData);
            }
            if (p.ebayData) {
              initEbayData[p.productUri] = p.ebayData;
              setEbayData(initEbayData);
            }
        });

        setEditedData(initEdited);
        setNewFields(initNewFields);
        setImages(initImages);
        setShopifyCats(initShopifyCats);
        setEbayCats(initEbayCats);
        setEbayCatOptions(initEbayCatOptions);
        setOriginalShopifyCats(JSON.parse(JSON.stringify(initShopifyCats)));
        setOriginalEbayCats(JSON.parse(JSON.stringify(initEbayCats)));

        // baseline (deep clones)
        setOriginalEditedData(JSON.parse(JSON.stringify(initEdited)));
        setOriginalImages(JSON.parse(JSON.stringify(initImages)));
        setLoading(false);
      }, false);
    };

  useEffect(() => {
    
    fetchData();
  }, [page, customerId, productUri]);

  const normalize = (v) => {
    if (v == null) return "";
    if (typeof v === "string") return v.trim();
    return v; // numbers/booleans as-is
  };

  const isEqualSpecs = (a = {}, b = {}) => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const k of new Set([...aKeys, ...bKeys])) {
      if (normalize(a[k]) !== normalize(b[k])) return false;
    }
    return true;
  };

  const isEqualBg = (a = [], b = []) =>
    a.length === b.length &&
    a.every((img, i) => b[i] && b[i].displayType === img.displayType);

  // Optional: return which fields changed (useful for UI badges)
  const diffSpecs = (a = {}, b = {}) => {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    const changed = [];
    for (const k of keys) {
      if (normalize(a[k]) !== normalize(b[k])) changed.push(k);
    }
    return changed;
  };

  const handleFieldChange = (productUri, field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [productUri]: {
        ...prev[productUri],
        [field]: value,
      },
    }));
  };
  const isProductDirty = (productUri) => {
    const nowSpecs = editedData[productUri] || {};
    const baseSpecs = originalEditedData[productUri] || {};
    const nowBg = images[productUri] || [];
    const baseBg = originalImages[productUri] || [];
    const shopifyChanged = (shopifyCats[productUri] || '') !== (originalShopifyCats[productUri] || '');
    const ebayChanged = (ebayCats[productUri] || '') !== (originalEbayCats[productUri] || '');
    return !isEqualSpecs(nowSpecs, baseSpecs) || !isEqualBg(nowBg, baseBg) || shopifyChanged || ebayChanged;
  };

  const anyDirty = React.useMemo(
    () => Object.keys(editedData).some((uri) => isProductDirty(uri)),
    [editedData, images, originalEditedData, originalImages]
  );

  const renderDynamicFields = (productUri) => {
    const fields = editedData[productUri] || {};
    return Object.entries(fields).map(([key, value]) => {
      if (key === 'Condition') {
        return (
          <FormControl key={key} fullWidth margin="dense" size="small">
            <InputLabel>{key}</InputLabel>
            <Select
              label={key}
              value={value || ''}
              onChange={(e) => handleFieldChange(productUri, key, e.target.value)}
              renderValue={(selected) => conditionDisplayLabel(selected)}
            >
              {Object.entries(conditionToValueMap).map(([rawKey, label]) => (
                <MenuItem key={rawKey} value={rawKey}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
      if (key === 'Category' || key === 'PrimaryCategory') {
        // Category editing is disabled for now — see the eBay Category field below.
        return (
          <TextField
            key={key}
            label={key}
            value={value || ''}
            fullWidth
            margin="dense"
            size="small"
            InputProps={{ readOnly: true }}
          />
        );
      }
      return (
        <React.Fragment key={key}>
          <TextField
            label={key}
            value={value}
            onChange={(e) => handleFieldChange(productUri, key, e.target.value)}
            fullWidth
            margin="dense"
            size="small"
            multiline={key === "Description"}
            rows={key === "Description" ? 3 : undefined}
          />
          {key === "Price" && (
            <Button
              onClick={() => viewProductPricing(productUri)}
              variant="contained"
              sx={{ backgroundColor: '#111111', '&:hover': { backgroundColor: '#000000' }, marginTop: '20px' }}
            >
              View Detailed Pricing
            </Button>
          )}
        </React.Fragment>
      );
    });
  };

  const renderAddFieldRow = (productUri) => {
    const local = newFields[productUri] || { field: "", value: "" };

    return (
      <Box display="flex" gap={1} alignItems="center" mt={1}>
        <TextField
          label="New Field"
          value={local.field}
          onChange={(e) =>
            setNewFields((prev) => ({
              ...prev,
              [productUri]: { ...prev[productUri], field: e.target.value },
            }))
          }
          size="small"
        />
        <TextField
          label="Value"
          value={local.value}
          onChange={(e) =>
            setNewFields((prev) => ({
              ...prev,
              [productUri]: { ...prev[productUri], value: e.target.value },
            }))
          }
          size="small"
        />
        <Button
          variant="outlined"
          onClick={() => {
            if (!local.field) return;
            handleFieldChange(productUri, local.field, local.value);
            setNewFields((prev) => ({
              ...prev,
              [productUri]: { field: "", value: "" },
            }));
          }}
        >
          Add
        </Button>
      </Box>
    );
  };

  const handleBulkSave = async () => {
    for (const [productUri, updates] of Object.entries(editedData)) {
    
    }

    alert("Changes saved.");
  };

  const ebayUrl = (productId) => {
      const data = ebayData[productId];
      return process.env.REACT_APP_EBAY_PRODUCT_URL + '/' + data[0].listingId ; 
  }
  const handleProductSave = async (productUri, specs) => {
    await saveProductSpecs(customerId, productUri, specs);
    await saveProductImages(customerId, productUri, images[productUri]);

    const newShopifyCat = shopifyCats[productUri] || '';
    if (newShopifyCat !== (originalShopifyCats[productUri] || '')) {
      const nameParts = newShopifyCat.split(' > ');
      await saveProductShopifyCategory(customerId, productUri, {
        categoryId: SHOPIFY_CATEGORY_OPTIONS[newShopifyCat] || '',
        fullName: newShopifyCat,
        name: nameParts[nameParts.length - 1],
      });
    }

    const newEbayCat = ebayCats[productUri] || '';
    if (newEbayCat !== (originalEbayCats[productUri] || '')) {
      const options = ebayCatOptions[productUri] || [];
      await saveProductEbayCategory(customerId, productUri, newEbayCat, {
        primaryCategory: originalEbayCats[productUri] || '',
        allPotentialCategories: options,
      });
    }

    setOriginalImages(images);
    setOriginalEditedData(editedData);
    setOriginalShopifyCats(shopifyCats);
    setOriginalEbayCats(ebayCats);
    setOpen(true);
  };

  const handleAddPhotos = (productUri) => {
    addPhotoTargetRef.current = productUri;
    addPhotoInputRef.current.value = null;
    addPhotoInputRef.current.click();
  };

  const handlePhotoFilesSelected = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const productUri = addPhotoTargetRef.current;
    setUploadingPhotos((prev) => ({ ...prev, [productUri]: true }));
    setUploadError(null);
    try {
      const images = await Promise.all(
        files.map(async (file) => {
          let convertedFile = file;
          if (isHeicFile(file)) {
            const jpegBlob = await convertHEICToJPEG(file);
            if (jpegBlob) {
              convertedFile = new File([jpegBlob], file.name.replace(/\.[^/.]+$/, ".jpg"), { type: "image/jpeg" });
            }
          }
          const resized = await resizeImage(convertedFile);
          return encodeFileAsBase64(resized);
        })
      );
      await addProductImages(customer.apiKey, productUri, images);
      setOpen(true);
    } catch (err) {
      setUploadError(extractErrorMessage(err, 'Failed to upload photos'));
    } finally {
      setUploadingPhotos((prev) => ({ ...prev, [productUri]: false }));
    }
  };

  const handleProductDiscard = async (productUri) => {
    fetchData();
  };

  const handleDiscard = async () => {
    fetchData();
  };
  return (
    <Box sx={{ padding: 2 }}>
      <input
        ref={addPhotoInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handlePhotoFilesSelected}
      />
      <Typography variant="h4" gutterBottom>
         {productUri ? "Edit Product" : "Edit Products"}
      </Typography>
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell colSpan={5}>Editable Fields</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((product) => (
                <TableRow key={product.productUri} hover>
                  <TableCell>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
                        gap: 1,
                        maxWidth: 400, // Adjust or remove this to allow wider layout
                      }}
                    >
                       {(images[product.productUri] || [])
                          .filter((im) => !im.isHidden)                      // don't show hidden
                          .map((img, index, visibleArr) => {
                            const onlyOneLeft = visibleArr.length <= 1;
                          const displayType = img.displayType;
                          const showBg =
                            displayType === 1;
                          const url = showBg
                            ? img.urlString.replace("noBackground", "original")
                            : img.urlString.replace("original", "noBackground");

                        return (
                          <ProductImageCell
                            key={product.productUri + index}
                            src={url}
                            hasBackground={showBg}
                            onToggleBackground={() => {
                              setImages(prev => {
                                const prevArr = prev[product.productUri] || [];
                                const newArr = prevArr.map((image, i) =>
                                  i === index
                                    ? {
                                        ...image,
                                        displayType:
                                          image.displayType === 0 || image.displayType === "original"
                                            ? 1 // or "noBackground"
                                            : 0 // or "original"
                                      }
                                    : image
                                );
                                return { ...prev, [product.productUri]: newArr };
                              });
                            }}                            
                           onDelete={() => requestDelete(product.productUri, img)}
                            showDelete={!onlyOneLeft}    
                          />
                        );
                      })}
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!!uploadingPhotos[product.productUri]}
                        onClick={() => handleAddPhotos(product.productUri)}
                        sx={{ mt: 1 }}
                      >
                        {uploadingPhotos[product.productUri] ? 'Uploading...' : 'Add Photos'}
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell colSpan={5}>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <FormControl fullWidth margin="dense" size="small">
                        <InputLabel>Shopify Category</InputLabel>
                        <Select
                          label="Shopify Category"
                          value={shopifyCats[product.productUri] || ''}
                          onChange={(e) => setShopifyCats((prev) => ({ ...prev, [product.productUri]: e.target.value }))}
                          MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                        >
                          {SHOPIFY_CATEGORY_NAMES.map((opt) => (
                            <MenuItem key={opt} value={opt} sx={{ fontSize: '0.85rem', whiteSpace: 'normal' }}>{opt}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {(ebayCatOptions[product.productUri] || []).length > 0 && (
                        <TextField
                          label="eBay Category"
                          value={ebayCats[product.productUri] || ''}
                          fullWidth
                          margin="dense"
                          size="small"
                          InputProps={{ readOnly: true }}
                        />
                      )}
                      {renderDynamicFields(product.productUri)}
                      {renderAddFieldRow(product.productUri)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={3}>
                    { isProductDirty(product.productUri) ? (
                      <>
                    <Button variant="contained" color="primary" onClick={() => handleProductSave(product.productUri, editedData[product.productUri])}>
                      Save
                    </Button>
                    <Button variant="outlined" color="inherit" onClick={() => handleProductDiscard(product.productUri)}>
                      Discard Changes
                    </Button>
                    </>) : (
                      <>   
                    <Button
                        onClick={() => handleRetry(product.productUri)}
                        sx={{ padding: '10px 20px', backgroundColor: '#111111', '&:hover': { backgroundColor: '#000000' }, gap: 1 }}
                        variant="contained"
                        disabled={isSubmitting}
                    >
                    {submittingAction === 'retry' && <CircularProgress size={16} color="inherit" />}
                    Retry
                    </Button>
                    {customerHasShopify(customer) && (product.shopifyData ? (
                    <Button
                    onClick={() => window.open(product.shopifyArray[0].uri, '_blank')}
                    sx={{ padding: '10px 20px', backgroundColor: '#111111', '&:hover': { backgroundColor: '#000000' } }}
                    variant="contained"
                    disabled={isSubmitting}
                    >
                    View Shopify Listing
                    </Button>
                    ) : (
                      <Button
                          onClick={() => handleShopifyPost(product.productUri)}
                          sx={{ padding: '10px 20px', backgroundColor: '#111111', '&:hover': { backgroundColor: '#000000' }, gap: 1 }}
                          variant="contained"
                          disabled={isSubmitting}
                      >
                      {submittingAction === 'shopify' && <CircularProgress size={16} color="inherit" />}
                      Post to Shopify
                      </Button>
                    )
                    )}
                  {customerHasEbay(customer) && (product.ebayData ? (
                    <Button
                    onClick={() => window.open(ebayUrl(product.productUri), '_blank')}
                    sx={{ padding: '10px 20px', backgroundColor: '#111111', '&:hover': { backgroundColor: '#000000' } }}
                    variant="contained"
                    disabled={isSubmitting}
                    >
                    View eBay Listing
                    </Button>
                    ) : (
                      <Button
                          onClick={() => handleEbayPost(product.productUri)}
                          sx={{ padding: '10px 20px', backgroundColor: '#111111', '&:hover': { backgroundColor: '#000000' }, gap: 1 }}
                          variant="contained"
                          disabled={isSubmitting}
                      >
                      {submittingAction === 'ebay' && <CircularProgress size={16} color="inherit" />}
                          Post to eBay
                      </Button>
                    )
                    )}
                    {customerHasSmartsheet(customer) && (product.smartsheetRowId ? (
                    <Button
                    onClick={() => window.open('https://smartsheet.com', '_blank')}
                    sx={{ padding: '10px 20px', backgroundColor: '#111111', '&:hover': { backgroundColor: '#000000' } }}
                    variant="contained"
                    disabled={isSubmitting}
                    >
                    Open Smartsheet
                    </Button>
                    ) : (
                      <Button
                          onClick={() => {handleSmartsheetPost(product.productUri)}}
                          sx={{ padding: '10px 20px', backgroundColor: '#111111', '&:hover': { backgroundColor: '#000000' }, gap: 1 }}
                          variant="contained"
                          disabled={isSubmitting}
                      >
                      {submittingAction === 'smartsheet' && <CircularProgress size={16} color="inherit" />}
                          Post to Smartsheet
                      </Button>
                    )
                    )}
                    </>
                    )}
                    <IconButton
                      sx={{
                        padding: 0,            // zero padding
                        minWidth: 'auto',      // for <Button>
                        lineHeight: 1,         // tight line height
                        '&:hover': { backgroundColor: 'transparent' },
                      }}
                      onClick={() => {
                        setProductToDelete(product);
                        setConfirmOpen(true);
                      }}
                      color="#111111"
                    >
                      <DeleteIcon fontSize="large" />
                    </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}


      {!productUri && (
      <Box display="flex" justifyContent="space-between" mt={2}>
        {/* <Button
          onClick={handleBulkSave}
          variant="contained"
          color="primary"
          disabled={Object.keys(editedData).length === 0}
        >
          Save Changes
        </Button> */}
        <Pagination
          count={Math.ceil(totalItems / pageSize)}
          page={page}
          onChange={(e, val) => setPage(val)}
        />
      </Box>
      )}
      {/* <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          display: "flex",
          gap: 2,
          zIndex: 1000, // stays above other stuff
        }}
      >
        <Button variant="outlined" color="inherit" onClick={handleDiscard}>
          Discard
        </Button>
        <Button variant="contained" color="primary" onClick={handleBulkSave}>
          Save Changes
        </Button>
      </Box> */}
       <ConfirmDeleteDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          await deleteProduct(customerId, productToDelete.productUri);
          setConfirmOpen(false);
          fetchData();
        }}
        itemName={ productToDelete?.title ?? "this item"}
      />
      <ConfirmDeleteDialog 
        open={imageConfirmOpen}
        onClose={() => setImageConfirmOpen(false)}
        onConfirm={async () => {
          handleConfirmDelete();
          setImageConfirmOpen(false);
        }}
        itemName={"this photo"}
      />
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setOpen(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
          Changes saved
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!uploadError}
        autoHideDuration={5000}
        onClose={() => setUploadError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setUploadError(null)} severity="error" variant="filled" sx={{ width: "100%" }}>
          {uploadError}
        </Alert>
      </Snackbar>
    </Box>
  );
}